/**
 * recurrenceSummary — human-readable recurrence summary line.
 *
 * Mirrors legacy `genereteSummaryText` (`calendar-event.js:2224-2295`) and the
 * fixed weekday predicate originally in `calendar.model.js:285`. Shared by the
 * Edit form's live summary (`EventFormBody`) and the View modal's recurrence
 * field (`EventViewModal`).
 *
 * Accepts both the form-state shape (CalendarDateTime / CalendarDate values)
 * and the raw API event shape (epoch seconds + 'YYYY-MM-DD' string + comma-
 * separated `daysOfWeek`).
 */

import { cdtToJSDate, epochToCalendarDateTime } from './dateTime.js';

const WEEK_DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function formatSummaryDate(date) {
	if (!date) return '';
	try {
		return date.toLocaleDateString(undefined, {
			year: 'numeric', month: 'short', day: 'numeric'
		});
	} catch {
		return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
	}
}

function isRecurring(v) {
	return v === true || v === '1' || v === 1;
}

function normalizeDays(raw) {
	if (Array.isArray(raw)) return raw.map(String);
	if (typeof raw === 'string' && raw.trim()) {
		return raw.split(',').map(s => s.trim()).filter(Boolean);
	}
	return [];
}

function getStartDayOfMonth(startTime, tz) {
	if (startTime == null) return null;
	// CalendarDateTime
	if (typeof startTime === 'object' && startTime.day != null) return startTime.day;
	// Epoch seconds (API)
	if (typeof startTime === 'number' || /^\d+$/.test(String(startTime))) {
		const cdt = epochToCalendarDateTime(Number(startTime), tz);
		return cdt?.day ?? null;
	}
	return null;
}

function getEndDateAsJS(recurringEndDate, tz) {
	if (recurringEndDate == null) return null;
	// CalendarDate (form state)
	if (typeof recurringEndDate === 'object' && recurringEndDate.year != null) {
		return cdtToJSDate(recurringEndDate, tz);
	}
	// 'YYYY-MM-DD' string (API)
	if (typeof recurringEndDate === 'string') {
		const m = recurringEndDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
		if (!m) return null;
		return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
	}
	return null;
}

/**
 * @param {Object} input Form-state-like OR API event with the recurrence fields.
 * @param {string} [tz='UTC'] IANA timezone for epoch / CalendarDate conversion.
 * @returns {string} Summary text, or empty string when not recurring.
 */
export function formatRecurrenceSummary(input, tz = 'UTC') {
	if (!input || !isRecurring(input.recurring)) return '';
	const repsInterval = input.repsInterval != null ? String(input.repsInterval) : '1';
	const recurringType = input.recurringType || 'day';
	const daysOfWeek = normalizeDays(input.daysOfWeek);
	const repsType = input.repsType || 'number';
	const reps = input.reps != null ? String(input.reps) : '';
	const isOne = repsInterval === '1';

	let text = '';
	if (isOne) {
		text += recurringType === 'day'
			? 'Daily, '
			: recurringType === 'weeks'
				? 'Weekly on '
				: 'Monthly on ';
	} else {
		const unit = recurringType === 'day'
			? 'days, '
			: recurringType === 'weeks'
				? 'weeks on '
				: 'months on ';
		text += `Every ${repsInterval || '1'} ${unit}`;
	}
	if (recurringType === 'weeks') {
		if (daysOfWeek.length === 7) {
			text += 'all days, ';
		} else if (
			daysOfWeek.length === 5
			&& daysOfWeek.indexOf('0') === -1
			&& daysOfWeek.indexOf('6') === -1
		) {
			text += 'weekdays, ';
		} else {
			const sorted = [...daysOfWeek].sort();
			const labels = sorted
				.map(k => WEEK_DAYS_SHORT[Number(k)] || '')
				.filter(Boolean);
			if (labels.length) text += `${labels.join(', ')}, `;
		}
	} else if (recurringType === 'month') {
		const day = getStartDayOfMonth(input.startTime, tz);
		if (day) text += `day ${day}, `;
	}
	if (repsType === 'number') {
		const r = reps.length > 0 ? reps : '0';
		text += `${r} times`;
	} else {
		const endD = getEndDateAsJS(input.recurringEndDate, tz);
		text += ` until ${endD ? formatSummaryDate(endD) : ''}`;
	}
	return text;
}
